<script setup lang="ts">
import {computed, ref} from "vue";
import MedicationCheckupCopiloteLine from "@/medication-checkup/components/MedicationCheckupCopiloteLine.vue";
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupInterviewSheetCard from "@/medication-checkup/components/MedicationCheckupInterviewSheetCard.vue";
import MedicationCheckupCopiloteBubble from "@/medication-checkup/components/MedicationCheckupCopiloteBubble.vue";
import MedicationCheckupSceneReplies from "@/medication-checkup/components/MedicationCheckupSceneReplies.vue";
import {MedicationCheckupAction} from "@/medication-checkup/interfaces";
import {
    InterviewSheetAnswer,
    InterviewSheetAnswers,
    InterviewSheetQuestion,
    InterviewSheetView
} from "@/medication-checkup/interfaces/interview-sheet";
import {SceneProps, useScene} from "@/medication-checkup/composables/useScene";
import {useMissingAnswers} from "@/medication-checkup/composables/useMissingAnswers";
import {asksYesNo, isUnanswered, takesChoices} from "@/medication-checkup/services/InterviewSheetRules";

/**
 * The scene of one part of the interview sheet — the course plays it three
 * times, once per part, each block serving its own words.
 *
 * Everything it shows is served by the handler: the transition line, the
 * title and questions of the part, the answers the collection proposes and —
 * once validated — the answers given. Answering is the scene's own state, as
 * in the Girerd questionnaire, laid over the proposals: the closing action,
 * held until every question has its answer, submits them all at once — a
 * click while one is missing points at it instead. The texts are optional,
 * and so are the free text questions and the treatments named.
 */

const props = defineProps<SceneProps<InterviewSheetView>>();

const {bubbleShown, cardShown, clicked, view, text, actions, acted, act: submit} = useScene(
    props,
    {card: true}
);

/**
 * The answers given so far, by question ref — local until the closing
 * action submits them. Resets with the scene, as the selection of the
 * collection does.
 */
const answers = ref<InterviewSheetAnswers>({});

const questions = computed(() => view.value?.questions ?? []);

/**
 * The answers the collection proposed, served on the live block, by question
 * ref.
 */
const proposals = computed<InterviewSheetAnswers>(() => {
    return Object.fromEntries(questions.value.flatMap(question => {
        return question.answer ? [[question.ref, {yes_no: question.answer.yes_no, text: question.answer.text}]] : [];
    }));
});

/**
 * What each question reads as answered: the pharmacist's act when there is
 * one, the proposal otherwise. The local state only ever holds the acts, so
 * a refreshed block keeps them and the proposals stay the server's.
 */
const effective = computed<InterviewSheetAnswers>(() => ({...proposals.value, ...answers.value}));

/**
 * The refs of the questions still waiting on an answer.
 */
const missing = computed(() => questions.value
    .filter(question => isUnanswered(question, effective.value[question.ref]))
    .map(question => question.ref));

const root = ref<HTMLElement | null>(null);

const {blocked, flagged, warning, flag} = useMissingAnswers(missing, root);

/**
 * Answer one question — local only: the closing action submits them all.
 */
const answer = (ref: string, given: InterviewSheetAnswer) => {
    answers.value = {...answers.value, [ref]: given};
};

/**
 * An answer as the handler expects it. A question answered by picking sends
 * the options picked, its text being what was typed for « Autre »; a
 * question naming treatments sends them on a « yes ».
 */
const toSubmitted = (question: InterviewSheetQuestion) => {
    const given = effective.value[question.ref];
    const yesNo = asksYesNo(question) ? given?.yes_no ?? null : null;

    if (takesChoices(question)) {
        return {question_ref: question.ref, yes_no: yesNo, choices: given?.choices ?? [], text: given?.other?.trim() || null};
    }

    return {
        question_ref: question.ref,
        yes_no: yesNo,
        text: given?.text?.trim() || null,
        ...question.lines && yesNo === true ? {line_ids: given?.line_ids ?? []} : {}
    };
};

/**
 * Take the closing action, submitting the answers of the part.
 */
const act = (action: MedicationCheckupAction) => submit(action, {
    answers: questions.value.map(toSubmitted)
});
</script>

<template>
  <div ref="root" class="flex flex-col gap-3.5">
    <template v-if="frozen">
      <MedicationCheckupCopiloteLine v-if="text" :text="text" />
      <MedicationCheckupInterviewSheetCard v-if="view" :view="view" frozen />
      <MedicationCheckupUserEcho v-if="acted" :text="acted" />
    </template>

    <template v-else>
      <MedicationCheckupCopiloteBubble
        :shown="bubbleShown"
        :text="text"
      />

      <MedicationCheckupInterviewSheetCard
        v-if="cardShown && view"
        :view="view"
        :answers="effective"
        :flagged="flagged"
        class="animate-message-in"
        @answer="answer"
      />

      <MedicationCheckupSceneReplies
        :clicked="clicked"
        :actions="actions"
        :shown="cardShown"
        :blocked="blocked"
        :warning="warning"
        @act="act"
        @blocked="flag"
      />
    </template>
  </div>
</template>
